// ============================================================================
// DynamicRoute — parte del feat_2026-05-24_rediseno-navegacion-backoffice
// ============================================================================
//
// Catch-all que resuelve en runtime qué componente montar para la ruta actual.
//
// Algoritmo:
//   1. Lee el menú del usuario (menuStore — ya cargado por AuthGuard tras login).
//   2. Aplana el árbol y busca el item cuya `url` matchea `location.pathname`.
//   3. Si el item tiene `viewKey`, busca en viewRegistry el componente y
//      aplica RoutePermissionGuard con el permiso del item.
//   4. Si no hay match, muestra una pantalla de "ruta no asociada".
//
// El menú es la fuente de verdad de qué URLs existen — si una URL no está
// en sys_menu_items, no existe en el frontend.
// ============================================================================

import { Suspense } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useMenuStore } from 'store/menuStore';
import { useAuthStore } from 'store/authStore';
import { resolveView } from 'routes/viewRegistry';
import { usePermission } from 'hooks/usePermission';
import Loader from 'components/Loader';
import { PROFILE } from 'constants/profileKeys';

interface FlatItem {
  url: string;
  viewKey: string | null;
  permission: string | null;
}

function flatten(items: any[]): FlatItem[] {
  const out: FlatItem[] = [];
  const walk = (node: any) => {
    if (node.type === 'item' && node.url) {
      out.push({
        url: node.url,
        viewKey: node.viewKey ?? null,
        permission: node.permission ?? null,
      });
    }
    if (Array.isArray(node.children)) node.children.forEach(walk);
  };
  items.forEach(walk);
  return out;
}

export default function DynamicRoute() {
  const location = useLocation();
  const menu     = useMenuStore((s) => s.items);
  const isAuth   = useAuthStore((s) => s.isAuthenticated());
  const keyProfile = useAuthStore((s) => s.user?.keyProfile);
  const can      = usePermission();

  if (!isAuth) return <Navigate to="/login" replace />;

  // Usuarios "tipo dashboard" no usan el backoffice: cualquier ruta los rebota
  // al panel full-screen GeoAudit (que vive fuera del MainLayout, en /panel).
  if (keyProfile === PROFILE.DASHBOARD) return <Navigate to="/panel" replace />;

  // El menú se carga asíncronamente tras login. Mientras esté vacío, mostrar loader.
  if (!menu || menu.length === 0) return <Loader />;

  const flat = flatten(menu as any[]);
  const match = flat.find((i) => i.url === location.pathname);

  if (!match) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          <h5 className="alert-heading">
            <i className="ph ph-warning-circle me-2" />
            Ruta no asociada a vista
          </h5>
          <p className="mb-1">
            La ruta <code>{location.pathname}</code> no corresponde a ningún
            item del menú con vista asignada.
          </p>
          <p className="mb-0 text-muted small">
            Posibles causas: el item del menú se desactivó, la URL se cambió
            desde el administrador, o falta el campo <code>view_key</code>.
          </p>
        </div>
      </div>
    );
  }

  // Permiso requerido (si el item lo define)
  if (match.permission && !can(match.permission)) {
    return <Navigate to="/" replace />;
  }

  const Component = resolveView(match.viewKey);
  if (!Component) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h5 className="alert-heading">
            <i className="ph ph-x-circle me-2" />
            Vista no registrada
          </h5>
          <p className="mb-1">
            El menú apunta a <code>view_key = "{match.viewKey}"</code> pero esa
            clave no existe en <code>viewRegistry.ts</code>.
          </p>
          <p className="mb-0 text-muted small">
            Solución: registrar el componente en
            <code> backoffice/src/routes/viewRegistry.ts</code> o corregir el
            <code> view_key</code> del menu item.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <Component />
    </Suspense>
  );
}
